import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';

import { ApWindowService } from '@ap/cdk';
import { ApContentService } from '@ap/content';
import { ApPersistenceService } from '@ap/persistence';

import { MijnRoutes } from './mijn-navigatie.constant';
import content from './mijn-navigatie.content.json';
import {
    MijnNavigatieHistory,
    MijnNavigatieHistoryName,
    MijnNavigatieRoute,
    MijnNavigationSessionName,
    MijnRouteKey
} from './mijn-navigatie.model';

@Injectable({ providedIn: 'root' })
export class MijnNavigatieService {
    public constructor(
        @Inject(DOCUMENT) private readonly document: Document,
        private readonly apWindowService: ApWindowService,
        private readonly apContentService: ApContentService,
        private readonly apPersistenceService: ApPersistenceService,
        private readonly activatedRoute: ActivatedRoute,
        private readonly router: Router
    ) {
        this.apContentService.addContentItems(content);
    }

    public static getRouteKeyForUrl(url: string): MijnRouteKey {
        const mijnUrl = MijnNavigatieService.getCleanMijnUrl(url);
        const [routeKey] = [...MijnRoutes.entries()].find(([, value]) => value.regex.test(mijnUrl)) || [];
        return routeKey;
    }

    public static isSameUrl(url1: string, url2: string): boolean {
        const cleanedUrl1 = MijnNavigatieService.getCleanMijnUrl(url1);
        const cleanedUrl2 = MijnNavigatieService.getCleanMijnUrl(url2);
        return cleanedUrl1 === cleanedUrl2;
    }

    private static getCleanMijnUrl(url: string): string {
        if (url.match(/^.*\/#$/)) {
            // eslint-disable-next-line no-param-reassign
            url = url.replace('#', '');
        }
        if (url.indexOf('#') < 0) {
            const baseUri = document.baseURI;
            return `/${url.substring(url.indexOf(baseUri) + baseUri.length).split('?')?.[0]}`;
        }
        return url.split('#')?.[1]?.split('?')?.[0];
    }

    private static getPathParam(url: string): string {
        const cleanUrl = MijnNavigatieService.getCleanMijnUrl(url);
        const pathArray = cleanUrl.split('/');
        return pathArray.filter((urlPart) => /^\d+$/.test(urlPart))[0] ?? null;
    }

    private static getQueryParamsFromUrl(url: string): Params {
        const urlParamsPart = url.split('?')?.[1];
        if (urlParamsPart) {
            const params: Params = {};
            new URLSearchParams(urlParamsPart).forEach((value, key) => {
                params[key] = value;
            });
            return params;
        }
        return {};
    }

    private static currentRouteIsSecondToLastRoute(route: MijnNavigatieRoute, currentHistory: MijnNavigatieHistory): boolean {
        if (currentHistory.routes.length > 1) {
            const secondToLastRoute = currentHistory.routes[currentHistory.routes.length - 2];
            return MijnNavigatieService.isSameUrl(secondToLastRoute.url, route.url);
        }
        return false;
    }

    public getRouteKeyForPath(path: string): MijnRouteKey {
        if (this.isMijnUrl(path)) {
            const baseUrl = this.apContentService.get('mijn-navigatie.mijn-omgeving.baseUrl');
            const mijnUrl = path.substring(path.indexOf(baseUrl) + baseUrl.length).split('?')?.[0];
            const [routeKey] = [...MijnRoutes.entries()].find(([, value]) => value.regex.test(mijnUrl)) || [];
            return routeKey;
        }
        return null;
    }

    public navigate(mijnRouteKey: MijnRouteKey, detail?: Params | string): void {
        const mijnOmgevingElement = this.document.querySelector('mijn-omgeving');
        if (mijnOmgevingElement == null) {
            throw new Error('mijn-omgeving element not found');
        }
        const navigationEvent = MijnRoutes.get(mijnRouteKey).navigationEvent;
        let event: CustomEvent;
        if (typeof detail === 'string') {
            event = new CustomEvent(navigationEvent, { bubbles: true, detail });
        } else {
            event = new CustomEvent(navigationEvent, { bubbles: true, ...(detail && { detail }) });
        }
        mijnOmgevingElement.dispatchEvent(event);
    }

    public navigateByUrl(url: string): void {
        const currentPageIsMijnUrl = this.isMijnUrl(this.apWindowService.location_href() as string);
        const routeKey = MijnNavigatieService.getRouteKeyForUrl(url);
        if (routeKey && currentPageIsMijnUrl) {
            const queryParams = MijnNavigatieService.getQueryParamsFromUrl(url);
            const pathParam = MijnNavigatieService.getPathParam(url);
            const params = {
                ...queryParams,
                ...(pathParam && { path: pathParam })
            };
            if (Object.keys(params).length > 0) {
                this.navigate(routeKey, params);
            } else {
                this.navigate(routeKey);
            }
        }
        this.apWindowService.location_href(url);
    }

    public storeUrl(url: string, reset = false, breadcrumb: string = null): void {
        const isMijnUrl = this.isMijnUrl(url);
        if (isMijnUrl && !url.match(/\/([0-9]+)\/(?:\1)(\/|$)/)) {
            const route: MijnNavigatieRoute = { url, breadcrumb: breadcrumb ?? this.getBreadcrumbForUrl(url) };
            const currentHistory = this.getHistory();

            // when navigating internally the mijn-omgeving routerstate is not updated,
            // here the mijn-omgeving routerstate is updated with the current url.
            if (!route.url.endsWith(this.router.routerState.snapshot.url)) {
                this.router.navigate([MijnNavigatieService.getCleanMijnUrl(route.url)], {
                    skipLocationChange: true,
                    fragment: '',
                    relativeTo: this.activatedRoute
                });
            }

            if (reset) {
                this.resetHistory(route, currentHistory);
            } else if (currentHistory?.routes.length > 0) {
                this.updateCurrentHistory(currentHistory, route);
            } else {
                this.setInitialHistory(route);
            }
        }
    }

    public isMijnUrl(url: string): boolean {
        return url.includes(this.apContentService.get('mijn-navigatie.mijn-omgeving.baseUrl'));
    }

    public removeLastRoute(): void {
        const currentHistory = this.getHistory();
        if (currentHistory?.routes.length > 1) {
            this.removeLastRouteFromHistory(currentHistory);
        }
    }

    public getHistory(): MijnNavigatieHistory {
        const currentHistory: MijnNavigatieHistory = this.apPersistenceService.getSession(
            MijnNavigationSessionName,
            MijnNavigatieHistoryName
        );
        return currentHistory ?? { routes: [], enabled: true };
    }

    public clearRouteContainingId(id: string): void {
        const history = this.getHistory();
        history.routes = history.routes.filter((route) => !route.url.includes(id));
        this.storeHistory(history);
    }

    public clearHistory(): void {
        this.apPersistenceService.clearSession(MijnNavigationSessionName);
    }

    private getBreadcrumbForUrl(url: string): string {
        const routeKey = MijnNavigatieService.getRouteKeyForUrl(url);
        return this.apContentService.get(`mijn-navigatie.breadcrumb.${routeKey}`);
    }

    private setInitialHistory(route: MijnNavigatieRoute): void {
        // enabled true is kept here for backward compatibility, some applications use an older version that require this boolean,
        this.storeHistory({ routes: [route], enabled: true } as MijnNavigatieHistory);
    }

    private updateCurrentHistory(currentHistory: MijnNavigatieHistory, route: MijnNavigatieRoute): void {
        const previousRoute = currentHistory.routes[currentHistory.routes.length - 1];
        if (MijnNavigatieService.isSameUrl(previousRoute.url, route.url)) {
            this.replaceLastRouteInHistory(route, currentHistory);
        } else if (MijnNavigatieService.currentRouteIsSecondToLastRoute(route, currentHistory)) {
            this.removeLastRoute();
        } else {
            this.addRouteToHistory(route, currentHistory);
        }
    }

    private replaceLastRouteInHistory(route: MijnNavigatieRoute, history: MijnNavigatieHistory): void {
        const newHistory = { ...history };
        newHistory.routes.pop();
        newHistory.routes.push(route);
        this.storeHistory(newHistory);
    }

    private addRouteToHistory(route: MijnNavigatieRoute, history: MijnNavigatieHistory): void {
        const newHistory = { ...history };
        newHistory.routes.push(route);
        this.storeHistory(newHistory);
    }

    private removeLastRouteFromHistory(history: MijnNavigatieHistory): void {
        const newHistory = { ...history };
        newHistory.routes.pop();
        this.storeHistory(newHistory);
    }

    private resetHistory(route: MijnNavigatieRoute, history: MijnNavigatieHistory): void {
        const newHistory = { ...history, routes: [route] };
        this.storeHistory(newHistory);
    }

    private storeHistory(history: MijnNavigatieHistory): void {
        this.apPersistenceService.setSession(MijnNavigationSessionName, MijnNavigatieHistoryName, history);
    }
}
